import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { SecureCredentialsService } from '@/services/secureCredentialsService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TokenRefreshService } from '@/services/tokenRefreshService';

interface ConnectionStatus {
  isConnected: boolean;
  isConnecting: boolean;
  needsRefresh: boolean;
  error?: string;
}

interface ServiceSettings {
  calendar_enabled: boolean;
  outlook_enabled: boolean;
}

export const useMicrosoftConnection = () => {
  const { session } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    isConnected: false,
    isConnecting: false,
    needsRefresh: false
  });
  const [serviceSettings, setServiceSettings] = useState<ServiceSettings>({
    calendar_enabled: true,
    outlook_enabled: true
  });

  const checkConnectionStatus = useCallback(async () => {
    if (!session?.user?.id) return;

    try {
      // First check if tokens need refresh
      const refreshResult = await TokenRefreshService.refreshTokensIfNeeded(session.user.id);
      
      if (!refreshResult.success && refreshResult.needsReauth) {
        setConnectionStatus({
          isConnected: false,
          isConnecting: false,
          needsRefresh: true,
          error: 'Your Microsoft account connection has expired. Please reconnect.'
        });
        return;
      }

      const status = await SecureCredentialsService.validateCredentials(session.user.id);
      setConnectionStatus({
        isConnected: status.isValid,
        isConnecting: false,
        needsRefresh: status.needsRefresh,
        error: status.error
      });

      // Load service settings if connected
      if (status.isValid) {
        const { data } = await supabase
          .from('microsoft365_credentials')
          .select('calendar_enabled, outlook_enabled')
          .eq('user_id', session.user.id)
          .single();

        if (data) {
          setServiceSettings({
            calendar_enabled: data.calendar_enabled,
            outlook_enabled: data.outlook_enabled
          });
        }
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
      setConnectionStatus({
        isConnected: false,
        isConnecting: false,
        needsRefresh: false,
        error: 'Failed to check connection status'
      });
    }
  }, [session?.user?.id]);

  const initiateConnection = useCallback(async () => {
    if (!session?.user?.id) {
      toast.error('Please log in to connect Microsoft 365');
      return;
    }

    setConnectionStatus(prev => ({ ...prev, isConnecting: true, error: undefined }));

    try {
      // Generate state parameter for security
      const state = crypto.randomUUID();
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes from now

      // Store state in database
      const { error: stateError } = await supabase
        .from('auth_states')
        .insert({
          state,
          user_id: session.user.id,
          provider: 'microsoft365',
          expires_at: expiresAt.toISOString()
        });

      if (stateError) {
        throw new Error('Failed to initialize OAuth flow');
      }

      // Microsoft OAuth URL with proper scopes
      const clientId = 'your-microsoft-client-id'; // This should come from environment
      const redirectUri = `${window.location.origin}/settings?tab=communication`;
      const scopes = [
        'openid',
        'profile',
        'email',
        'offline_access',
        'Mail.Read',
        'Mail.Send',
        'Mail.ReadWrite',
        'Calendars.ReadWrite',
        'User.Read'
      ].join(' ');

      const authUrl = new URL('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('scope', scopes);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('response_mode', 'query');
      authUrl.searchParams.set('prompt', 'consent');

      // Redirect to Microsoft OAuth
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Error initiating Microsoft connection:', error);
      setConnectionStatus(prev => ({
        ...prev,
        isConnecting: false,
        error: error instanceof Error ? error.message : 'Failed to connect'
      }));
      toast.error('Failed to initiate Microsoft 365 connection');
    }
  }, [session?.user?.id]);

  const disconnect = useCallback(async () => {
    if (!session?.user?.id) return;

    try {
      await SecureCredentialsService.removeCredentials(session.user.id);
      setConnectionStatus({
        isConnected: false,
        isConnecting: false,
        needsRefresh: false
      });
      setServiceSettings({
        calendar_enabled: true,
        outlook_enabled: true
      });
      toast.success('Microsoft 365 account disconnected');
    } catch (error) {
      console.error('Error disconnecting Microsoft account:', error);
      toast.error('Failed to disconnect Microsoft 365 account');
    }
  }, [session?.user?.id]);

  const updateServiceSetting = useCallback(async (service: 'calendar' | 'outlook', enabled: boolean) => {
    if (!session?.user?.id) return;

    try {
      const field = service === 'calendar' ? 'calendar_enabled' : 'outlook_enabled';
      
      const { error } = await supabase
        .from('microsoft365_credentials')
        .update({ [field]: enabled })
        .eq('user_id', session.user.id);

      if (error) throw error;

      setServiceSettings(prev => ({
        ...prev,
        [field]: enabled
      }));

      toast.success(`${service === 'calendar' ? 'Calendar' : 'Email'} access ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error(`Error updating ${service} setting:`, error);
      toast.error(`Failed to update ${service} setting`);
    }
  }, [session?.user?.id]);

  return {
    connectionStatus,
    serviceSettings,
    initiateConnection,
    disconnect,
    updateServiceSetting,
    checkConnectionStatus
  };
};
